import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useClientUsers } from '../../hooks/useClientUsers';
import { formatClientOptionLabel } from '../../utils/clientTenantLabel';
import AdminWebhookConfig from '../LiveStamping/AdminWebhookConfig';
import AdminWebhookDeliveryLog from '../LiveStamping/AdminWebhookDeliveryLog';
import {
  adminTestFireWebhook,
  fetchWebhookAnalytics,
  fetchWebhookPublicKeys,
} from '../../services/liveStamping';

const ADV_EVENT_TYPES = [
  'order.placed',
  'order.executing',
  'order.metadata_ready',
  'order.completed',
  'order.rejected',
];

const isProd = process.env.REACT_APP_ENV === 'production';

function ClientTenantDropdown({ value, onChange, clients, loading }) {
  const sorted = useMemo(
    () =>
      [...clients].sort((a, b) =>
        formatClientOptionLabel(a).localeCompare(formatClientOptionLabel(b), undefined, {
          sensitivity: 'base',
        })
      ),
    [clients]
  );

  return (
    <div className="max-w-xl">
      <label className="block text-sm font-medium text-gray-700 mb-2">Client (tenant)</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-60"
      >
        <option value="">{loading ? 'Loading clients…' : 'Select a client…'}</option>
        {sorted
          .filter((c) => c.id || c._id)
          .map((c) => {
            const id = String(c.id ?? c._id);
            return (
              <option key={id} value={id}>
                {formatClientOptionLabel({ ...c, id })}
              </option>
            );
          })}
      </select>
      <p className="mt-2 text-xs text-gray-500">Shown as Client name · email or company fallback (tenant UUID).</p>
    </div>
  );
}

function formatUtcShort(isoLike) {
  if (!isoLike) return '';
  try {
    const d = typeof isoLike === 'string' ? new Date(isoLike) : isoLike;
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'UTC',
    }).format(d);
  } catch {
    return String(isoLike);
  }
}

function WebhookAnalyticsPanel() {
  const DAYS = 7;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await fetchWebhookAnalytics(DAYS);
      setData(res || null);
    } catch (e) {
      setErr(e.message || 'Failed to load analytics');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Delivery analytics</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Last{' '}
            <span className="font-semibold tabular-nums">{DAYS}</span> rolling days · outcome time (
            <span className="font-mono text-xs">processedAt</span>, UTC) ·{' '}
            <span className="font-mono text-xs">GET /api/admin/webhooks/analytics</span>
          </p>
          {data && (
            <p className="text-xs text-gray-400 mt-1">
              Range: {formatUtcShort(data.periodStartUtc)} — {formatUtcShort(data.periodEndUtc)}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {err && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{err}</div>
      )}

      {loading && !data ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : data ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-lg border border-emerald-100 bg-emerald-50/70 px-4 py-3">
            <div className="text-xs font-medium text-emerald-800 uppercase tracking-wide">Delivered</div>
            <div className="text-3xl font-semibold text-emerald-900 tabular-nums mt-1">
              {typeof data.deliveredCount === 'number' ? data.deliveredCount : '—'}
            </div>
          </div>
          <div className="rounded-lg border border-red-100 bg-red-50/70 px-4 py-3">
            <div className="text-xs font-medium text-red-800 uppercase tracking-wide">Failed</div>
            <div className="text-3xl font-semibold text-red-900 tabular-nums mt-1">
              {typeof data.failedCount === 'number' ? data.failedCount : '—'}
            </div>
          </div>
          <div className="rounded-lg border border-indigo-100 bg-indigo-50/70 px-4 py-3">
            <div className="text-xs font-medium text-indigo-800 uppercase tracking-wide">
              Tenants with failures
            </div>
            <div className="text-3xl font-semibold text-indigo-900 tabular-nums mt-1">
              {typeof data.failedTenantDistinctCount === 'number' ? data.failedTenantDistinctCount : '—'}
            </div>
            <p className="text-[11px] text-indigo-700/90 mt-1.5 leading-snug">
              Distinct client tenants with ≥1 failure (excludes system events).
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function WebhookPublicKeysPanel() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await fetchWebhookPublicKeys();
      setKeys(res.keys || []);
    } catch (e) {
      setErr(e.message || 'Failed to load keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Public signing keys</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">GET /api/public-keys</span>
            {' '}— clients use these PEMs to verify <span className="font-mono">x-matrica-signature</span>.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      {err && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{err}</div>
      )}
      {loading && keys.length === 0 ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : keys.length === 0 ? (
        <p className="text-sm text-gray-500">No keys configured on the server.</p>
      ) : (
        <div className="space-y-4">
          {keys.map((k) => (
            <div key={k.code} className="border border-gray-100 rounded-lg p-4 bg-gray-50/80">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="font-mono text-sm font-semibold text-gray-800">{k.code}</span>
                {k.isDeprecated && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">deprecated</span>
                )}
              </div>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all font-mono max-h-40 overflow-y-auto bg-white border border-gray-200 rounded p-3">
                {k.publicKey}
              </pre>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(k.publicKey);
                  toast.success('Public key copied');
                }}
                className="mt-2 text-xs text-indigo-600 hover:text-indigo-800"
              >
                Copy PEM
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdvancedAdminTestFire({ tenantId }) {
  const [eventType, setEventType] = useState('order.completed');
  const [eventSubType, setEventSubType] = useState('stamping');
  const [entityName, setEntityName] = useState('live_stamping_order');
  const [entityId, setEntityId] = useState('');
  const [externalRefId, setExternalRefId] = useState('');
  const [payloadJson, setPayloadJson] = useState('{\n  "note": "admin synthetic"\n}');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const tid = (tenantId || '').trim();
    if (!tid) {
      toast.error('Select a client above before sending a synthetic event.');
      return;
    }
    let payload = {};
    const raw = payloadJson.trim();
    if (raw) {
      try {
        payload = JSON.parse(raw);
        if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
          toast.error('Payload must be a JSON object');
          return;
        }
      } catch {
        toast.error('Invalid JSON in payload');
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await adminTestFireWebhook({
        tenantId: tid,
        eventType,
        eventSubType: eventSubType.trim() || undefined,
        entityName: entityName.trim() || undefined,
        entityId: entityId.trim() || `adv-test-${Date.now()}`,
        externalRefId: externalRefId.trim(),
        payload: Object.keys(payload).length ? payload : {},
      });
      toast.success(res.message || 'Synthetic event created');
    } catch (err) {
      toast.error(err.message || 'Test fire failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Advanced synthetic event</h2>
      <p className="text-sm text-gray-500 mb-4">
        <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">POST /api/admin/webhooks/test-fire</span>
        {' '}— optional fields override defaults. Immediate delivery applies after enqueue.
      </p>
      {isProd ? (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Test fire is disabled in production for this endpoint.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-xs text-gray-500">
            Tenant is the client selected in <span className="font-medium text-gray-700">Tenant webhook</span> above.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Event type *</label>
              <select
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {ADV_EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Event sub-type</label>
              <input
                value={eventSubType}
                onChange={(e) => setEventSubType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Entity name</label>
              <input
                value={entityName}
                onChange={(e) => setEntityName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Entity ID</label>
              <input
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                placeholder="optional — random if empty"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">External ref ID</label>
              <input
                value={externalRefId}
                onChange={(e) => setExternalRefId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Payload (JSON object)</label>
            <textarea
              value={payloadJson}
              onChange={(e) => setPayloadJson(e.target.value)}
              rows={6}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
            />
          </div>
          <button
            type="submit"
            disabled={submitting || !tenantId.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Sending…' : 'Send synthetic event'}
          </button>
        </form>
      )}
    </div>
  );
}

/**
 * Admin dashboard: all webhook management APIs (/api/admin/webhooks/*, /api/public-keys).
 */
const ClientWebhooksAdmin = () => {
  const [tenantId, setTenantId] = useState('');
  const { clients, loading: clientsLoading } = useClientUsers();

  return (
    <div className="h-full overflow-y-auto p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-8 pb-16">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Client-Webhooks</h1>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            Admin tools for tenant webhook delivery: RSA public keys, enable/disable delivery per tenant,
            synthetic test events, and the delivery audit log ({' '}
            <span className="font-mono text-xs">PATCH /api/admin/webhooks/toggle</span>
            {' · '}
            <span className="font-mono text-xs">GET /api/admin/webhooks/events</span>
            {' · '}
            <span className="font-mono text-xs">POST …/replay</span>
            {' · '}
            <span className="font-mono text-xs">POST …/test-fire</span>
            ).
          </p>
        </div>

        <WebhookAnalyticsPanel />

        <WebhookPublicKeysPanel />

        <section className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Tenant webhook (toggle & quick test)</h2>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
            <ClientTenantDropdown
              value={tenantId}
              onChange={setTenantId}
              clients={clients}
              loading={clientsLoading}
            />
          </div>
          <AdminWebhookConfig tenantId={tenantId} />
        </section>

        <AdvancedAdminTestFire tenantId={tenantId} />

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Delivery log</h2>
          <AdminWebhookDeliveryLog clients={clients} clientsLoading={clientsLoading} />
        </section>
      </div>
    </div>
  );
};

export default ClientWebhooksAdmin;
